// src/components/extensions/ExtensionCard.tsx
//
// A single extension, rendered as a full-width showcase panel. Every extension
// on the page goes through this one component, and every part of it is optional
// in the config: the licence, the install line, the demo preview and the link
// row each appear only when the entry supplies them. An extension with no links
// renders no link row at all, rather than an empty button.
//
// A panel rather than a link itself. The outbound links inside are the
// navigation, and wrapping the whole panel in an anchor would put several
// destinations behind one control.
//
// The animation here is all entrance. Nothing loops, nothing idles, and nothing
// overshoots — the same rules the About page encodes in about/motion.ts. The
// pieces below declare `variants` with no `animate` prop of their own, so
// framer-motion sequences them off the panel's inherited state rather than
// each one running its own independent animation.
import { useReducedMotion, type Variants } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

import type { Extension, ExtensionLink } from '../../config/siteConfig';
import { DURATION, EASE_OUT, STAGGER, motionSet } from '../about/motion';
import { demoFor } from './demos';
import { iconFor } from './icons';
import {
  Panel,
  IconTile,
  IconGlyph,
  Content,
  IndexNumeral,
  MetaRow,
  PlatformText,
  MetaDivider,
  CategoryText,
  MetaBadge,
  CardTitle,
  CardDescription,
  FeatureList,
  FeatureItem,
  DemoSlot,
  DemoFigure,
  CardFooter,
  InstallLine,
  InstallLabel,
  InstallCommand,
  TechList,
  TechTag,
  LinkList,
  LinkItem,
  LinkAnchor,
  VisuallyHidden,
} from './ExtensionCard.styles';

/**
 * The icon gets its own entrance rather than a plain fade: it arrives with a
 * slight settle from a rotated, undersized start, which reads as the panel
 * "opening" onto it. Transform plus opacity only, like every other reveal, so it
 * cannot reflow the content beside it.
 */
const iconIn: Variants = {
  hidden: { opacity: 0, scale: 0.86, rotate: -10 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: { duration: DURATION.reveal, ease: EASE_OUT },
  },
};

/**
 * Reduced motion: the rotation and the scale are dropped and it fades in place.
 * Keeping the icon in the same slot either way means the reduced-motion page is
 * the same layout, just without the travel.
 */
const iconInReduced: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.revealReduced } },
};

interface ExtensionCardProps {
  extension: Extension;
  /** Position in the list, for the "01 of 02" numeral. Zero-based. */
  index: number;
  /** How many extensions there are in total. */
  total: number;
}

const ExtensionLinkItem = ({ link }: { link: ExtensionLink }) => {
  // Defaults to true: every link on this page points at another site.
  const isExternal = link.external !== false;

  return (
    <LinkItem>
      <LinkAnchor
        href={link.href}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {link.label}
        {isExternal && (
          <>
            <ArrowUpRight size={13} strokeWidth={2} aria-hidden="true" />
            <VisuallyHidden>(opens in a new tab)</VisuallyHidden>
          </>
        )}
      </LinkAnchor>
    </LinkItem>
  );
};

const ExtensionCard = ({ extension, index, total }: ExtensionCardProps) => {
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);

  const {
    id,
    title,
    platform,
    category,
    description,
    features,
    technologies,
    links,
    license,
    installCommand,
    demoImage,
    demoImageAlt,
    icon,
    demo,
  } = extension;

  const titleId = `${id}-title`;
  const Icon = iconFor(icon);
  const Demo = demoFor(demo);

  /* Tabular numerals keep the glyph from shifting as the count grows, and the
     pad keeps the two columns aligned from the first entry. */
  const ordinal = String(index + 1).padStart(2, '0');
  const count = String(total).padStart(2, '0');

  const iconVariants = reduceMotion ? iconInReduced : iconIn;

  return (
    <Panel variants={anim.group(STAGGER.list)} aria-labelledby={titleId}>
      {/* Decorative: the heading below is what names the extension, so the
          glyph is hidden from assistive technology. */}
      <IconTile variants={iconVariants} aria-hidden="true">
        <IconGlyph>
          <Icon size={26} strokeWidth={1.6} />
        </IconGlyph>
      </IconTile>

      {/* A sequencing node in its own right, not a plain wrapper: the reveal
          variants below are declared on elements inside it, and an explicit
          stagger parent is what guarantees they are driven from the panel
          instead of sitting unanimated. IconTile is a direct child of Panel and
          needs no such node of its own. */}
      <Content variants={anim.group(STAGGER.list)}>
        <MetaRow variants={anim.reveal}>
          {/* Decorative. "01 / 02" reads as "zero one slash zero two", and the
              order it conveys is already carried by the list itself, so it is
              for the eye only. */}
          <IndexNumeral aria-hidden="true">
            {ordinal} / {count}
          </IndexNumeral>
          <MetaDivider aria-hidden="true">·</MetaDivider>
          <PlatformText>{platform}</PlatformText>
          <MetaDivider aria-hidden="true">/</MetaDivider>
          <CategoryText>{category}</CategoryText>
          {license && <MetaBadge>{license}</MetaBadge>}
        </MetaRow>

        <CardTitle id={titleId} variants={anim.reveal}>
          {title}
        </CardTitle>

        <CardDescription variants={anim.reveal}>{description}</CardDescription>

        {/* A list, so the count is announced rather than read as one run of text.
            The label is not visible: this is detail under the description, and a
            visible heading here would compete with the title. */}
        <FeatureList variants={anim.reveal} aria-label={`Features of ${title}`}>
          {features.map((feature) => (
            <FeatureItem key={feature}>{feature}</FeatureItem>
          ))}
        </FeatureList>

        {Demo && (
          <DemoSlot variants={anim.reveal}>
            <Demo />
          </DemoSlot>
        )}

        {demoImage && (
          <DemoFigure variants={anim.reveal}>
            <img
              src={demoImage}
              alt={demoImageAlt ?? ''}
              loading="lazy"
              decoding="async"
            />
          </DemoFigure>
        )}

        <CardFooter variants={anim.reveal}>
          {installCommand && (
            <InstallLine>
              <InstallLabel>Install</InstallLabel>
              <InstallCommand>{installCommand}</InstallCommand>
            </InstallLine>
          )}

          <TechList aria-label={`Technology used in ${title}`}>
            {technologies.map((technology) => (
              <TechTag key={technology}>{technology}</TechTag>
            ))}
          </TechList>

          {links.length > 0 && (
            <LinkList aria-label={`Links for ${title}`}>
              {links.map((link) => (
                <ExtensionLinkItem key={`${link.label}-${link.href}`} link={link} />
              ))}
            </LinkList>
          )}
        </CardFooter>
      </Content>
    </Panel>
  );
};

export default ExtensionCard;
